<template>
  <div>
    <consultant-role-promotion-sidebar />
    <div style="position: relative">
      <app-breadcrumb :breadcrumbTitle="false" />
    </div>
    <!-- Filtros -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Nome do Consultor" label-for="consultant-name"  class="mt-1">
              <b-form-input
                id="consultant-name"
                v-model="consultantName"
                @keyup.enter="search()"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group label="Status" label-for="status"  class="mt-1">
              <v-select
                id="status"
                v-model="status"
                :options="statusOptions"
                label="label"
                :reduce="(option) => option.key"
                multiple
                :loading="loadingFilters.statusOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" class="mb-md-0 mb-2 text-right">
            <b-button
              variant="outline-primary"
              @click.prevent="search"
              :disabled="loading"
            >
              <b-spinner v-if="loading" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>
                {{ loading ? "Aguarde..." : "Pesquisar" }}
              </span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search()"
            >
              <template #no-options="{ search, searching, loading }">
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>          
        </b-row>
      </div>

      <b-table
        hover
        ref="refPromotionsListTable"
        class="position-relative"
        :items="items"
        responsive
        :fields="tableColumns"
        :sort-by.sync="sortField"
        show-empty
        empty-text="Nenhum registro encontrado"
        :busy="loading"
        :primary-key="'id'"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-1" />
          </div>
        </template>

        <!-- Column: Consultor -->
        <template #cell(consultant_name)="data">
          <span class="font-weight-bold">{{ data.item.consultant_name }}</span>
        </template>

        <!-- Column: Nível Anterior -->
        <template #cell(old_role_name)="data">
          <div class="text-wrap">
            {{ data.item.old_role_name }}
          </div>
        </template>

        <!-- Column: Novo Nível -->
        <template #cell(new_role_name)="data">
          <div class="text-wrap">
            {{ data.item.new_role_name }}
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <div class="text-wrap">
            {{ data.item.status_label }}
            <b-badge
              v-if="data.item.certificate_pendency"
              v-b-tooltip.hover
              :title="data.item.certificate_pendency.message"
              variant="light-warning"
              class="ml-50"
            >
              {{ getCertificatePendencyLabel(data.item.certificate_pendency) }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Solicitado Por -->
        <template #cell(added_by_name)="data">
          <span>{{ data.item.added_by_name }}</span>
        </template>

        <!-- Column: Aprovado/Reprovado Por -->
        <template #cell(decided_by_name)="data">
          <div class="text-wrap">
            <span>{{ data.item.decided_by_name }}</span>
            <small v-if="data.item.decided_by_role_name" class="d-block text-muted">
              {{ data.item.decided_by_role_name }}
            </small>
          </div>
        </template>

        <!-- Column: Data da decisão -->
        <template #cell(decided_at)="data">
          <span v-if="data.item.decided_at">{{ data.item.decided_at | dateTime }}</span>
        </template>

        <!-- Column: Data -->
        <template #cell(created_at)="data">
          <span>{{ data.item.created_at | dateTime }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button-group>
            <b-button
              v-if="data.item.status == 'pending'"
              :id="`role-promotion-` + data.item.id"
              class="btn-icon mr-1"
              variant="flat"
              style="padding: 0"
              @click="editPromotion(data.item.id)"
            >
              <feather-icon
                class="text-body"
                :icon="$can(MANAGE_ACTION, APPROVE_CONSULTANT_ROLE) ? 'CheckSquareIcon' : 'EyeIcon'"
              />
            </b-button>
            <b-dropdown 
              variant="link" 
              no-caret 
              right 
              boundary="window"
              v-if="data.item.status == 'approved' && $can(MANAGE_ACTION, APPROVE_CONSULTANT_ROLE)"
              :disabled="isProcessing(data.item.id)"
            >
              <template #button-content>
                <b-spinner v-if="isProcessing(data.item.id)" small />
                <feather-icon
                  v-else
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                :disabled="isProcessing(data.item.id)"
                @click="processPromotion(data.item.id)"
              >
                <feather-icon icon="RepeatIcon" />
                <span class="align-middle ml-50">Processar mudança de nível</span>
              </b-dropdown-item>
            </b-dropdown>
          </b-button-group>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} a {{ pageItemsTo }} de
              {{ totalItems }} registros
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
  BFormGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import AppBreadcrumb from "@core/layouts/components/AppBreadcrumb.vue";
import ConsultantRolePromotionSidebar from "../components/ConsultantRolePromotionSidebar.vue";
import Ripple from "vue-ripple-directive";
import { APPROVE_CONSULTANT_ROLE, MANAGE_ACTION } from "@/constants/resources";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BSpinner,
    BFormGroup,
    vSelect,
    AppBreadcrumb,
    ConsultantRolePromotionSidebar,
  },
  directives: {
    Ripple,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      APPROVE_CONSULTANT_ROLE,
      MANAGE_ACTION,
      loading: false,
      processingIds: [],
      loadingFilters: {
        statusOptions: false,
      },
      currentPage: 1,
      itemsPerPage: 10,
      consultantName: undefined,
      status: undefined,
      sortField: null,
      tableColumns: [
        { key: "consultant_name", label: "Consultor", sortable: true },
        { key: "old_role_name", label: "Nível Anterior", sortable: false },
        { key: "new_role_name", label: "Novo Nível", sortable: false },
        { key: "status", label: "Status", sortable: true },
        { key: "added_by_name", label: "Solicitado Por", sortable: false },
        { key: "decided_by_name", label: "Aprovado/Reprovado Por", sortable: false },
        { key: "decided_at", label: "Data da decisão", sortable: false },
        { key: "created_at", label: "Data de solicitação", sortable: true },
        { key: "actions", label: "Ações"},
      ],
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.ROLE_PROMOTION_SEARCH_RESULT,
      statusOptions: types.ROLE_PROMOTION_STATUS_TYPES,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items: function () {
      return this.searchResult?.data || [];
    },
    totalItems: function () {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom: function () {
      return this.searchResult?.from || 0;
    },
    pageItemsTo: function () {
      return this.searchResult?.to || 0;
    },
  },
  async mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    await this.search();
    
    this.loadingFilters.statusOptions = true;
    this.getStatusOptions()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as opções de status. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loadingFilters.statusOptions = false;
      });

    if (this.$route.query.edit) {
      this.editPromotion(this.$route.query.edit);
    }
  },
  methods: {
    ...mapActions({
      searchPromotions: types.SEARCH_ROLE_PROMOTIONS,
      getStatusOptions: types.GET_ROLE_PROMOTION_STATUS_TYPES,
      openRolePromotionSidebar: types.OPEN_ROLE_PROMOTION_SIDEBAR,
      processConsultantRolePromotion: types.PROCESS_CONSULTANT_ROLE_PROMOTION,
    }),
    search(currentPage) {
      this.loading = true;
      this.currentPage = Number.isInteger(currentPage) ? currentPage : 1;

      return this.searchPromotions({
        consultant_name: this.consultantName,
        status: this.status,
        currentPage: this.currentPage,
        limit: this.itemsPerPage,
      })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar as solicitações. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editPromotion(id) {
      this.openRolePromotionSidebar({
        id,
        saveAction: () => this.search(this.currentPage),
      });
    },
    isProcessing(id) {
      return this.processingIds.includes(id);
    },
    processPromotion(id) {
      if (!this.$can(MANAGE_ACTION, APPROVE_CONSULTANT_ROLE)) {
        return;
      }
      if (this.isProcessing(id)) return;
      this.processingIds.push(id);

      this.toast({
        component: ToastificationContent,
        props: {
          title: "Processamento iniciado",
          text: "A mudança de nível está sendo processada e pode levar alguns minutos. Não processe novamente, aguarde a mensagem de conclusão.",
          icon: "InfoIcon",
          variant: "info",
        },
      });

      this.processConsultantRolePromotion(id)
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "A mudança de nível foi processada com sucesso.",
              icon: "CheckCircleIcon",
              variant: "success",
            },
          });
          // Mantém a linha bloqueada até a lista recarregar com o novo status,
          // senão o menu volta a ficar clicável antes de virar "completed".
          return this.search(this.currentPage);
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao processar a mudança de nível. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.processingIds = this.processingIds.filter((pid) => pid !== id);
        });
    },
    getCertificatePendencyLabel(pendency) {
      switch (pendency.consultant_certificate_status) {
        case null:
          return "Certificado não solicitado";
        case "pending":
          return "Certificado aguardando aprovação";
        case "rejected":
          return "Certificado reprovado";
        default:
          return "Certificado pendente";
      }
    },
    getStatusVariant(status) {
      const variants = {
        pending: "light-warning",
        approved: "light-success",
        rejected: "light-danger",
      };
      return variants[status] || "light-secondary";
    },
    formatDate(date) {
      return formatDate(date);
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
